/**
 * useConsentSync — Kullanıcı giriş yaptıktan sonra consent tercihlerini DB ile senkronize eder.
 *
 * Akış:
 *  1. Login sonrası GET /api/Consent/my → DB'deki tercihler localStorage'a yüklenir
 *  2. Kullanıcı banner'dan onay verince POST /api/Consent/save → DB'ye kaydedilir
 *  3. ch-consent-updated eventi dinlenerek her consent değişikliği otomatik DB'ye yazılır
 */
import { useEffect } from "react";
import axios from "axios";
import { APP_CONFIG } from "../constants";
import { hasDecided, getConsent } from "../utils/consentStore";
import { setAnalyticsConsent } from "../firebaseAnalytics";

// DB'den consent tercihlerini çekip localStorage'a yükle
const loadConsentFromDb = async (): Promise<void> => {
    const token = localStorage.getItem("collectohub-auth-token") || localStorage.getItem("token") || localStorage.getItem("ch_auth_token");
    if (!token) {
        // Misafir kullanıcı: Eğer yerelde henüz bir onay kararı yoksa banner'ı tetikle
        if (!hasDecided()) {
            window.dispatchEvent(new CustomEvent("ch-show-consent-banner"));
        }
        return;
    }

    try {
        const res = await axios.get(`${APP_CONFIG.API_URL}/api/Consent/my`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.success && Array.isArray(res.data?.data?.records)) {
            const records: Array<{ consentType: string; isGranted: boolean }> = res.data.data.records;

            if (records.length === 0) {
                // Yerel store'u sıfırlayıp kararsız durumuna getirelim ki banner tetiklensin
                localStorage.removeItem("ch_cookie_consent");
                window.dispatchEvent(new CustomEvent("ch-show-consent-banner"));
                return;
            }

            const analytics = records.find(r => r.consentType === "analytics")?.isGranted ?? false;
            const marketing = records.find(r => r.consentType === "marketing")?.isGranted ?? false;
            const personalizedContent = records.find(r => r.consentType === "personalized_content")?.isGranted ?? false;
            const marketingNotifications = records.find(r => r.consentType === "marketing_notifications")?.isGranted ?? false;

            // localStorage'a yükle (event dispatch etmez — sonsuz döngü önlemi)
            const consent = {
                necessary: true as const,
                analytics,
                marketing,
                personalizedContent,
                marketingNotifications,
                decidedAt: Date.now(),
                version: "2.0",
            };
            localStorage.setItem("ch_cookie_consent", JSON.stringify(consent));

            // Analytics başlat (eğer izin varsa)
            void setAnalyticsConsent(analytics);
        }
    } catch (err: any) {
        console.error("[ConsentSync] loadConsentFromDb error status:", err.response?.status, "message:", err.message);
        // Hata durumunda (örneğin 401 veya offline) eğer yerelde henüz karar yoksa banner'ı yine de göster
        if (!hasDecided()) {
            window.dispatchEvent(new CustomEvent("ch-show-consent-banner"));
        }
    }
};

// Consent değişikliğini DB'ye kaydet
const saveConsentToDb = async (consent: ReturnType<typeof getConsent>): Promise<void> => {
    const token = localStorage.getItem("collectohub-auth-token") || localStorage.getItem("token") || localStorage.getItem("ch_auth_token");
    if (!token) return;

    try {
        const policiesResponse = await axios.get(`${APP_CONFIG.API_BASE}/policies/active?languageCode=tr`);
        const explicitConsent = (policiesResponse.data?.data || []).find((item: any) => item.documentType === "explicit_consent");
        if (!explicitConsent?.id) {
            throw new Error("Aktif açık rıza metni bulunamadı; tercihler sunucuya kaydedilmedi.");
        }
        await axios.post(
            `${APP_CONFIG.API_URL}/api/Consent/save`,
            {
                consents: [
                    { consentType: "analytics", isGranted: consent.analytics },
                    { consentType: "marketing", isGranted: consent.marketing },
                    { consentType: "personalized_content", isGranted: consent.personalizedContent },
                    { consentType: "marketing_notifications", isGranted: consent.marketingNotifications },
                ],
                platform: "web",
                policyDocumentId: explicitConsent.id,
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    } catch (err: any) {
        console.error("[ConsentSync] saveConsentToDb error:", err.response?.status, err.response?.data || err.message);
    }
};

export const useConsentSync = () => {
    useEffect(() => {
        // 1. Ilk yuklemede calistir
        void loadConsentFromDb();

        // 2. Consent degisince DB'ye kaydet
        const handleConsentUpdated = (e: Event) => {
            const consent = (e as CustomEvent).detail;
            void saveConsentToDb(consent);
            void setAnalyticsConsent(consent.analytics === true);
        };

        // 3. Login/Logout/Auth status degisikligini yakala
        const handleAuthChange = () => {
            // LocalStorage güncellenmesi için ufak bir delay
            setTimeout(() => {
                void loadConsentFromDb();
            }, 100);
        };

        window.addEventListener("ch-consent-updated", handleConsentUpdated);
        window.addEventListener("auth-state-changed", handleAuthChange);

        return () => {
            window.removeEventListener("ch-consent-updated", handleConsentUpdated);
            window.removeEventListener("auth-state-changed", handleAuthChange);
        };
    }, []);
};
